<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    selectedDate: {
        type: Object,
        default: () => ({
            pickupDate: '',
            pickupTime: '',
            returnExpectedDate: '',
            returnExpectedTime: ''
        })
    },
    inputId: {
        type: String,
        default: ''
    },
    setFields: {
        type: Function,
        default: null
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: '0.75rem'
    },
    width: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const isReadonly = computed(() => props.readonly);
const isVisible = computed(() => props.visible);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const infoMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const isVehicleDataLoaded = ref(false);
const searchQuery = ref('');
const filteredOptions = ref([]);
const isLoading = ref(false);
const VEHICLE_CACHE_KEY = computed(() => {
    return `__VEHICLE_CACHE___${props.selectedDate.pickupDate}_${props.selectedDate.pickupTime}_${props.selectedDate.returnExpectedDate}_${props.selectedDate.returnExpectedTime}`;
});
const vehicleData = ref([]);
const byNo = ref(new Map());

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const isDateInvalid = computed(() => {
    return !props.selectedDate?.pickupDate || !props.selectedDate?.pickupTime || !props.selectedDate?.returnExpectedDate || !props.selectedDate?.returnExpectedTime;
});

function debounce(fn, wait = 150) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
    };
}

const _doFilter = () => {
    if (!vehicleData.value) return;
    const q = (searchQuery.value || '').toLowerCase();
    filteredOptions.value = vehicleData.value.filter((o) => o.value.toLowerCase().includes(q) || o.model.toLowerCase().includes(q) || o.fuel_type.toLowerCase().includes(q));
};
const filterOptions = debounce(_doFilter, 150);

onMounted(async () => {
    await searchVehicle();
    if (props.modelValue) {
        const found = byNo.value.get(String(props.modelValue));
        if (found) emitSelected(found);
    }
});

const searchVehicle = async () => {
    if (window[VEHICLE_CACHE_KEY.value]) {
        vehicleData.value = window[VEHICLE_CACHE_KEY.value];
        rebuildMap();
        filteredOptions.value = vehicleData.value;
        isVehicleDataLoaded.value = true;
        return;
    }

    isLoading.value = true;
    const config = {
        method: 'get',
        params: {
            startDate: props.selectedDate.pickupDate,
            startTime: props.selectedDate.pickupTime,
            endDate: props.selectedDate.returnExpectedDate,
            endTime: props.selectedDate.returnExpectedTime
        }
    };

    try {
        const { execute } = useAxios('/api/vehicle/contract', config, {
            immediate: false,
            onSuccess(response) {
                const fuelTypeList = commonCode('FUEL_TYPE').value;
                const vehicleStateList = commonCode('VEHICLE_STATE').value;

                const newList = response.data.data.map((item) => {
                    const fuelType = fuelTypeList.find((ft) => ft.value === item.fuel_type);
                    const vehicleState = vehicleStateList.find((vs) => vs.value === item.vehicle_state);
                    const hasRentalTxt = item.has_rental_list === 'Y' ? '등록' : '미등록';
                    return {
                        value: item.vehicle_no,
                        vehicle_id: String(item.vehicle_id || ''),
                        vehicle_group_id: String(item.vehicle_group_id || ''),
                        model: item.vehicle_model_name || '',
                        fuel_type: fuelType ? fuelType.text : '',
                        mileage: item.mileage || 0,
                        fuel_gauge: item.fuel_gauge || 0,
                        vehicle_state: vehicleState ? vehicleState.text : '',
                        has_rental_list_flag: item.has_rental_list,
                        has_rental_list: hasRentalTxt
                    };
                });

                window[VEHICLE_CACHE_KEY.value] = newList;
                vehicleData.value = newList;
                rebuildMap();
                filteredOptions.value = newList;
                isVehicleDataLoaded.value = true;
            },
            onError(err) {
                console.error('Error:', err.message);
                vehicleData.value = [];
                byNo.value = new Map();
                isVehicleDataLoaded.value = true;
            }
        });

        await execute();
    } finally {
        isLoading.value = false;
    }
};

function rebuildMap() {
    const m = new Map();
    for (const r of vehicleData.value) m.set(r.value, r);
    byNo.value = m;
}

function onInput(event) {
    const el = event.target;
    let value = el.value ?? '';

    if (value.length > props.maxlength) value = value.slice(0, props.maxlength);
    if (props.upper) value = value.toUpperCase();

    inputValue.value = value;
    infoMsg.value = '';
    ValidationMsg.value = '';
    errMessage.value = '';

    const found = byNo.value.get(value);
    if (found) {
        emitSelected(found);
        rentalListCheck();
        return;
    }

    emitUpdate(value);
}

function emitSelected(val) {
    const emitData = {
        id: props.inputId,
        value: val.value,
        vehicle_id: val.vehicle_id || '',
        vehicle_group_id: val.vehicle_group_id || '',
        model: val.model || '',
        fuel_type: val.fuel_type || '',
        mileage: val.mileage || 0,
        vehicle_state: val.vehicle_state || ''
    };
    if (typeof props.setFields === 'function') {
        props.setFields(emitData);
        console.log('일단테스트', emitData);
    }
    emit('update:modelValue', val.value);
    emit('input', emitData);
}

function emitUpdate(value) {
    inputValue.value = value;

    const payload = {
        id: props.inputId,
        value: value,
        vehicle_id: '',
        vehicle_group_id: '',
        model: '',
        fuel_type: '',
        mileage: 0,
        vehicle_state: ''
    };

    if (typeof props.setFields === 'function') {
        props.setFields(payload);
    }

    emit('update:modelValue', value);
    emit('input', payload);
}

const togglePopup = async () => {
    const isValid = props.selectedDate?.pickupDate && props.selectedDate?.pickupTime && props.selectedDate?.returnExpectedDate && props.selectedDate?.returnExpectedTime;

    if (!isValid) {
        ValidationMsg.value = '배차일시와 반납일시를 입력하세요.';
        return;
    }

    ValidationMsg.value = '';

    isPopupVisible.value = !isPopupVisible.value;

    if (isPopupVisible.value) {
        searchQuery.value = inputValue.value || '';
        filterOptions();
    }
};

const closePopup = () => {
    isPopupVisible.value = false;
};

const selectValue = (option) => {
    emitSelected(option);
    closePopup();
};

const clearInput = () => {
    ValidationMsg.value = '';
    errMessage.value = '';
    infoMsg.value = '';
    emit('update:modelValue', '');
    emit('input', {
        id: props.inputId,
        value: '',
        vehicle_id: '',
        vehicle_group_id: '',
        model: '',
        fuel_type: '',
        mileage: 0,
        vehicle_state: ''
    });
};

watch(
    () => props.modelValue,
    (newVehicleNo) => {
        inputValue.value = newVehicleNo || '';

        if (!newVehicleNo) {
            emitUpdate('');
            rentalListCheck();
            return;
        }

        const run = () => {
            const found = byNo.value.get(String(newVehicleNo));
            if (found) {
                emitSelected(found);
            } else {
                emitUpdate(newVehicleNo);
            }
            rentalListCheck();
        };

        if (isVehicleDataLoaded.value) {
            run();
        } else {
            const stop = watch(isVehicleDataLoaded, (ready) => {
                if (ready) {
                    run();
                    stop();
                }
            });
        }
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
        infoMsg.value = '';
    },
    { immediate: true, deep: true }
);

watch(
    inputValue,
    (newVal) => {
        if (!vehicleData.value) return;
        const found = vehicleData.value.find((v) => v.value === newVal);
        if (found && found.has_rental_list_flag === 'N') {
            infoMsg.value = '요금표 미등록 차량';
            ValidationMsg.value = '';
        } else {
            ValidationMsg.value = '';
        }
    },
    { immediate: true }
);

watch(
    () => props.selectedDate,
    async (newVal, oldVal) => {
        if (!newVal) return;

        const isValid = newVal.pickupDate && newVal.pickupTime && newVal.returnExpectedDate && newVal.returnExpectedTime;

        // 날짜 미입력 상태
        if (!isValid) {
            vehicleData.value = [];
            filteredOptions.value = [];
            byNo.value = new Map();
            isVehicleDataLoaded.value = false;

            ValidationMsg.value = '배차일시와 반납일시를 입력하세요.';
            return;
        }

        ValidationMsg.value = '';

        // 동일값이면 스킵
        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
            return;
        }

        vehicleData.value = [];
        filteredOptions.value = [];
        byNo.value = new Map();
        isVehicleDataLoaded.value = false;

        await searchVehicle();
    },
    {
        deep: true
    }
);

function rentalListCheck() {
    if (!inputValue.value || !vehicleData.value) return;
    const found = byNo.value.get(inputValue.value);
    if (found && found.has_rental_list_flag === 'N') {
        infoMsg.value = '요금표 미등록 차량';
        ValidationMsg.value = '';
    } else if (!props.errmsg) {
        ValidationMsg.value = '';
    }
}
</script>

<template>
    <div
        class="flex flex-col w-full"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            <!-- <span v-if="!ValidationMsg && infoMsg" class="info-message">{{ infoMsg }}</span> -->
        </label>
        <div class="relative w-full flex items-center h-12">
            <input
                v-if="isVisible"
                type="text"
                :id="inputId"
                v-model="inputValue"
                class="p-inputtext block-full w-full h-[2.5rem] ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': button,
                    'error-border': ValidationMsg
                }"
                :readonly="isReadonly"
                required
                :placeholder="placeholder"
                :required="required"
                @input="onInput"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button mr-[calc(6.5rem)]">&times;</button>

                <button v-if="!isReadonly" @click="togglePopup" class="button-style">검색</button>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <!-- <h3>선택</h3> -->
                    <input v-model="searchQuery" @input="filterOptions" placeholder="차량, 모델, 유종을 검색하세요" class="search-input" style="margin-top: 1rem" />
                    <div class="options-container">
                        <table class="vehicle-grid">
                            <thead>
                                <tr>
                                    <th>차량번호</th>
                                    <th>모델</th>
                                    <th>유종</th>
                                    <th>요금표등록</th>
                                    <th>상태</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                    <td>{{ option.value }}</td>
                                    <td>{{ option.model }}</td>
                                    <td>{{ option.fuel_type }}</td>
                                    <td :style="{ color: option.has_rental_list === '미등록' ? 'red' : 'inherit' }">
                                        {{ option.has_rental_list }}
                                    </td>
                                    <td :style="{ color: option.vehicle_state === '대기' ? 'inherit' : 'red' }">{{ option.vehicle_state }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.vehicle-grid th {
    position: sticky;
    top: 0;
    background-color: #f2f2f2;
    z-index: 10;
}
.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}
input[readonly] {
    background-color: whitesmoke !important;
    cursor: default;
}

input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}
.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}

.info-message {
    color: green;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.readonly-Bg {
    background-color: whitesmoke !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 40rem;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    text-align: left;
    min-width: 30rem;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.vehicle-grid {
    width: 100%;
    border-collapse: collapse;
}

.vehicle-grid th,
.vehicle-grid td {
    border: 0.1rem solid #ddd;
    border-left: none;
    border-right: none;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    text-align: center;
}

.vehicle-grid th {
    background-color: #f2f2f2;
    position: sticky;
    top: 0;
    z-index: 10;
}

.vehicle-grid td {
}

.vehicle-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.vehicle-grid th:hover {
    cursor: default;
}

.vehicle-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
